import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useDismissalCapture } from './Dialog';

/**
 * Edit-in-place (`FE-04.10`), card back's `InlineEdit`.
 *
 * **Considered as a `TextField` variant and rejected.** What is shared between
 * the card title, the list rename (`FE-11.x`) and the board title (`FE-13.x`)
 * is not the input, it is the *state machine*:
 *
 *   idle → editing → saving → error
 *
 * with revert-on-Escape, commit-on-Enter and reject-empty-and-restore-previous.
 * That is `FR-CARD-002`'s Behaviour verbatim — *"accepts multi-line text, saves
 * on blur or Enter, and reverts on Escape. An empty title is rejected — the
 * previous title is restored"* — plus AC2, AC3 and AC4.
 *
 * ⚠ **Escape must not reach the Dialog.** `FR-CARD-001` AC2 makes Escape close
 * the card back and `FR-CARD-002` makes it revert the edit; card back's D2
 * fixes the ladder — popover, then inline editor, then modal, one keystroke per
 * level. Radix's dismissable layer listens on `document`, so a React-only
 * `stopPropagation` is not enough on its own; this component stops the **native**
 * event too. See the test, which asserts against a document-level listener
 * rather than a React parent — a test on a parent `onKeyDown` passes while the
 * real Dialog still closes underneath.
 *
 * `readOnly` renders static text with **no affordance at all** — not a greyed
 * button, which invites a click and answers nothing (`FR-CARD-002` AC4).
 *
 * This renders its own bare control rather than composing `TextField`:
 * `TextField` ships a visible label above the input, and an inline title editor
 * that grew a second heading would be a different design.
 */

type Status = 'idle' | 'editing' | 'saving' | 'error';

export interface InlineEditProps {
  /** The committed value. This component never owns it. */
  value: string;
  /**
   * The accessible name of the field, and of the idle affordance ("Buy milk,
   * edit Card title"). Not rendered visibly — the value is what is on screen.
   */
  label: string;
  /**
   * Called with the trimmed new value. If it returns a promise, the component
   * drives `saving` itself and shows `error` on rejection; if it returns
   * `void`, the caller is expected to drive `busy` and `error` instead. Both
   * shapes work, and the second is what an optimistic mutation wants.
   */
  onCommit: (next: string) => void | Promise<void>;
  onCancel?: () => void;
  /** `FR-CARD-002` says blur *or* Enter, so `both` is the default. */
  commitOn?: 'enter' | 'blur' | 'both';
  multiline?: boolean;
  /** Multiline fields default to plain Enter as newline; card-back title opts into Enter-save. */
  multilineEnterKey?: 'newline' | 'commit';
  /**
   * The only policy there is. Named rather than implied so that a second one is
   * a prop change and not a rewrite.
   */
  emptyPolicy?: 'reject-and-restore';
  /**
   * Mount already editing, with the value selected — the *uncontrolled*
   * initial state, read once and never again.
   *
   * ⚠ Added by the parent in chunk `C3`, and the shape is the decision. Quick
   * edit (`FE-12.2`, `FR-BOARD-022`) opens by right-click, by a pencil, or by
   * `e` — **every one of them programmatic**, and this component has no way to
   * be told "start editing" from outside. `C2` hit the same wall from the other
   * side and paid for it: a `Rename` menu item was built and then **dropped**
   * because a menu could not open the editor without stranding focus on the
   * menu trigger (`C2-BRIEF.md` §12.2).
   *
   * `defaultEditing`, not `editing`: quick edit **mounts** when it opens and
   * **unmounts** when it closes, so an initial state is sufficient and there is
   * no second source of truth to keep in sync. A fully controlled prop would
   * have to answer what happens when the caller says `false` mid-save, and
   * nothing in this product needs to ask that yet.
   *
   * Additive and defaulted `false`, so every one of the 26 primitives'
   * shipped call sites behaves exactly as before.
   */
  defaultEditing?: boolean;
  /** Static text, no affordance. */
  readOnly?: boolean;
  /** Caller-driven in-flight state. Composes with the internal `saving`. */
  busy?: boolean;
  /** Caller-driven failure. Composes with a rejected `onCommit`. */
  error?: string;
  /** Shown muted when the value is empty. Also the idle accessible name. */
  placeholder?: string;
  className?: string;
  /** Class names for the idle/editing control itself. */
  controlClassName?: string;
}

export function InlineEdit({
  value,
  label,
  onCommit,
  onCancel,
  commitOn = 'both',
  multiline = false,
  multilineEnterKey = 'newline',
  emptyPolicy = 'reject-and-restore',
  defaultEditing = false,
  readOnly = false,
  busy,
  error,
  placeholder,
  className,
  controlClassName,
}: InlineEditProps) {
  /*
   * FE-80.1 — found by the profile-page and primitive slices' leak scans rather
   * than by a source audit, the way `primitives.textField.clear` was found by
   * the label picker's. `editSuffix` and `saveFailed` cannot be supplied by
   * callers, while `placeholder` can; when it is omitted, the fallback still
   * belongs to this primitive and resolves through the catalogue here. The
   * source values are byte-identical to what they replace, so the existing
   * accessible-name assertions spread across the calling surfaces do not move.
   */
  const { t } = useInterfaceLanguage();
  const [status, setStatus] = useState<Status>(defaultEditing ? 'editing' : 'idle');
  const [draft, setDraft] = useState(value);
  const [internalError, setInternalError] = useState<string | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const controlRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const returnFocus = useRef(false);

  const reactId = useId();
  const errorId = `${reactId}-error`;

  const editing = status !== 'idle';

  /**
   * Tell an enclosing `Dialog` not to dismiss while this edit is open.
   *
   * The `stopPropagation` pair in `handleKeyDown` below is necessary and **not
   * sufficient**, and the gap is invisible from this file. Radix's
   * `DismissableLayer` binds Escape on `ownerDocument` with `{ capture: true }`,
   * so it runs on the way DOWN and has already closed the dialog before the
   * event reaches this input — nothing a target handler does can un-run it.
   * This inverts the direction: the descendant declares itself, and the
   * ancestor declines to dismiss.
   *
   * A no-op outside a `Dialog`, so it is called unconditionally. Measured, not
   * reasoned: `composition.test.tsx` failed on this exact path before the line
   * existed, while every test in this file and in `Dialog.test.tsx` passed.
   */
  useDismissalCapture(editing);
  const saving = busy === true || status === 'saving';
  const shownError = error ?? internalError ?? undefined;
  const placeholderText = placeholder ?? t('primitives.inlineEdit.empty');

  // Only when the editor opens, not on every keystroke.
  useEffect(() => {
    if (!editing) return;
    const el = controlRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  // A commit that succeeded, or a value that changed underneath us over the
  // socket, becomes the draft the next edit starts from.
  useEffect(() => {
    if (status === 'idle') setDraft(value);
  }, [status, value]);

  useEffect(() => {
    if (editing) return;
    if (!returnFocus.current) return;
    returnFocus.current = false;
    triggerRef.current?.focus();
  }, [editing]);

  const close = useCallback(() => {
    returnFocus.current = true;
    setStatus('idle');
  }, []);

  const cancel = useCallback(() => {
    setDraft(value);
    setInternalError(null);
    close();
    onCancel?.();
  }, [close, onCancel, value]);

  const commit = useCallback(() => {
    if (saving) return;
    const next = draft.trim();

    // `reject-and-restore`: an empty value is not a value. The previous one
    // comes back and nothing is recorded (`FR-CARD-002` AC3).
    if (next === '' && emptyPolicy === 'reject-and-restore') {
      setDraft(value);
      setInternalError(null);
      close();
      return;
    }

    // Unchanged is not a change — AC2's "no change is recorded" applies to the
    // Escape path, and committing an identical value would write an event for
    // a member who only clicked in and out.
    if (next === value) {
      setDraft(value);
      close();
      return;
    }

    setInternalError(null);
    const result = onCommit(next);
    if (result instanceof Promise) {
      setStatus('saving');
      result.then(
        () => {
          close();
        },
        (reason: unknown) => {
          setInternalError(
            reason instanceof Error
              ? reason.message
              : t('primitives.inlineEdit.saveFailed'),
          );
          setStatus('error');
        },
      );
      return;
    }
    close();
    // ⚠ `t` IS in the dependency list, because this callback closes over it to
    // build the rejection message and omitting it would pin that message to
    // whatever language was live when the callback was last built. The
    // `t`-in-dep-list sweep, applicable in this file for the first time.
    //
    // The identity churn it costs is nil, and that was CHECKED rather than
    // assumed in both directions. `InterfaceLanguageProvider` calls
    // `contextValue(resolved)` inline in its render body with no `useMemo`, so
    // `t` is a new identity on EVERY provider render — not, as it is tempting
    // to write, only when the catalogue changes. That would matter if anything
    // consumed `commit` in an effect's dependency array; nothing does. It is
    // reached only from `handleKeyDown` and the blur handler, both of which
    // read the current value at call time.
  }, [close, draft, emptyPolicy, onCommit, saving, t, value]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // React's `stopPropagation` calls the native one, which stops the event
      // at React's root container — but a listener already attached to the same
      // node would still run, and Radix layers stack. Both, deliberately.
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      cancel();
      return;
    }

    if (event.key === 'Enter') {
      // In a multiline editor plain Enter is a newline; the modifier commits.
      if (
        multiline &&
        multilineEnterKey === 'newline' &&
        !(event.metaKey || event.ctrlKey)
      ) {
        return;
      }
      if (multiline && multilineEnterKey === 'commit' && event.shiftKey) return;
      if (commitOn === 'blur') return;
      event.preventDefault();
      commit();
    }
  }

  function handleBlur() {
    if (commitOn === 'enter') return;
    if (!editing || saving) return;
    commit();
  }

  if (readOnly) {
    // No affordance at all. Not a disabled button.
    return (
      <span className={cn('block text-ink', className)}>
        {value === '' ? <span className="text-ink-muted">{placeholderText}</span> : value}
      </span>
    );
  }

  if (!editing) {
    return (
      <span className={cn('block', className)}>
        <button
          type="button"
          ref={triggerRef}
          onClick={() => {
            setDraft(value);
            setInternalError(null);
            setStatus('editing');
          }}
          className={cn(
            'w-full rounded-card px-1 text-left text-ink',
            'hover:bg-surface-raised',
            controlClassName,
          )}
        >
          {value === '' ? (
            <span className="text-ink-muted">{placeholderText}</span>
          ) : (
            value
          )}
          {/* ⚠ The suffix stays a SUFFIX, and the value stays a visible text
              node beside it, because merging the two into one `aria-label`
              would take the member's own value out of the visible DOM. So a
              locale can translate the words and the punctuation but cannot put
              the verb before the value — recorded as a finding rather than
              fixed here, because fixing it restructures a primitive that
              fourteen surfaces render. */}
          <span className="sr-only">
            {t('primitives.inlineEdit.editSuffix', { label })}
          </span>
        </button>
        {shownError !== undefined && <ErrorLine id={errorId}>{shownError}</ErrorLine>}
      </span>
    );
  }

  const controlProps = {
    ref: controlRef as never,
    value: draft,
    'aria-label': label,
    'aria-busy': saving ? true : undefined,
    'aria-invalid': shownError !== undefined ? true : undefined,
    'aria-describedby': shownError !== undefined ? errorId : undefined,
    // `readOnly`, not `disabled`: a disabled control drops focus to <body>
    // mid-save, and the member loses their place in the card back.
    readOnly: saving,
    onChange: (event: { currentTarget: { value: string } }) => {
      setDraft(event.currentTarget.value);
    },
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    className: cn(
      'w-full rounded-card border border-border-strong bg-surface-raised px-1 text-ink',
      'focus:border-accent',
      shownError !== undefined && 'border-bad focus:border-bad',
      controlClassName,
    ),
  };

  return (
    <span className={cn('block', className)}>
      {multiline ? (
        <textarea {...controlProps} rows={2} />
      ) : (
        <input {...controlProps} type="text" />
      )}
      {shownError !== undefined && <ErrorLine id={errorId}>{shownError}</ErrorLine>}
    </span>
  );
}

function ErrorLine({ id, children }: { id: string; children: ReactNode }) {
  return (
    // Opaque sheet: the status colours and the muted scale both need one, and
    // this may sit on the translucent list panel.
    <span
      id={id}
      role="alert"
      className="mt-1 block rounded bg-surface-raised px-1 text-xs text-bad"
    >
      {children}
    </span>
  );
}
